import type React from 'react';
import { type CSSProperties, useState } from 'react';
import { type DropzoneState, useDropzone } from 'react-dropzone';

import { useUserTrialDetails } from '@revelio/auth';
import { Loading, PagePaths } from '@revelio/core';
import { Callout, Link, RevelioTooltip, Text } from '@revelio/ui';

import DataUnavailableBanner from '../../trialUser/dataUnavailableBanner';
import useIsTrialUserWithNoDatasetAccess from '../../trialUser/useIsTrialUserWithNoDatasetAccess';
import UploadIcon from '../data-enrichment/icons/UploadIcon';
import { DeliverableStepContainer } from '../deliverable-step-container';
import { DeliverablePaths } from '../deliverables-page';
import styles from './file-upload.module.css';
import layoutStyles from './upload-file-step.module.css';

const UploadFileStep = ({
  fileUploadHeight,
  onUpload,
  renderInDeliverablesContainer = false,
  sampleFileUrl,
  requiredColumnsTooltip,
  allowTrialUsersWithDatasetAccess,
}: {
  fileUploadHeight?: string;
  onUpload: (file: File) => Promise<void>;
  renderInDeliverablesContainer?: boolean;
  sampleFileUrl: string;
  requiredColumnsTooltip: string;
  allowTrialUsersWithDatasetAccess?: boolean;
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [acceptedDropError, setAcceptedDropError] = useState<string>();
  const { isTrialUser } = useUserTrialDetails();
  const isTrialWithNoDatasetAccess = useIsTrialUserWithNoDatasetAccess();

  const disableFileUpload = allowTrialUsersWithDatasetAccess
    ? !!isTrialWithNoDatasetAccess
    : !!isTrialUser;

  const dropzoneState = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxFiles: 1,
    // maxSize: maxFileSize,
    accept: {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
      'text/csv': ['.csv'],
    },
    onDropRejected: (fileRejections) => {
      fileRejections.forEach((fileRejection) => {
        setAcceptedDropError(
          `"${fileRejection.file.name}" must be either a .csv, .xls, or .xlsx`
        );
      });
    },
    onDropAccepted: async ([file]) => {
      setLoading(true);
      setAcceptedDropError(undefined);
      if (disableFileUpload) {
        setAcceptedDropError('This feature is not available for trial users');
        setLoading(false);
        return;
      }
      try {
        // @ts-expect-error noUncheckedIndexedAccess
        await onUpload(file);
      } catch (e) {
        setAcceptedDropError((e as Error).message);
      }
      setLoading(false);
    },
  });

  if (renderInDeliverablesContainer) {
    return (
      <DeliverableStepContainer
        id="upload_file"
        title="Upload File"
        startOverPath={`/${PagePaths.DELIVERABLES}/${DeliverablePaths.DATASET}`}
        submitButtonContent="Choose file"
        onSubmit={dropzoneState.open}
        disableFade
        isSubmitLoading={loading}
        isDataBuilder={false}
        banner={
          disableFileUpload && <DataUnavailableBanner featureUnavailable />
        }
        isSubmitDisabled={!!disableFileUpload}
        submitTooltip={
          disableFileUpload && 'This feature is not available for trial users'
        }
      >
        <FileUpload
          fileUploadHeight={fileUploadHeight}
          dropzoneState={dropzoneState}
          loading={loading}
          sampleFileUrl={sampleFileUrl}
          requiredColumnsTooltip={requiredColumnsTooltip}
          uploadErrorMessage={acceptedDropError}
        />
      </DeliverableStepContainer>
    );
  }

  return (
    <FileUpload
      fileUploadHeight={fileUploadHeight}
      dropzoneState={dropzoneState}
      loading={loading}
      sampleFileUrl={sampleFileUrl}
      requiredColumnsTooltip={requiredColumnsTooltip}
      uploadErrorMessage={acceptedDropError}
    />
  );
};

const FileUpload = ({
  fileUploadHeight,
  dropzoneState: {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    open,
  },
  loading,
  sampleFileUrl,
  requiredColumnsTooltip,
  uploadErrorMessage,
}: {
  fileUploadHeight?: string;
  dropzoneState: DropzoneState;
  loading: boolean;
  sampleFileUrl: string;
  requiredColumnsTooltip: string;
  uploadErrorMessage: string | undefined;
}) => {
  let dropState = 'idle';
  let message: React.ReactNode = (
    <div className={layoutStyles.dropPrompt}>
      <UploadIcon />
      <Text fontSize="inherit" className={layoutStyles.promptText}>
        Drag & drop your CSV or Excel file here,
      </Text>
      <Text fontSize="inherit" className={layoutStyles.promptText}>
        or{' '}
        <Link
          color="accent"
          underline="rest"
          className={styles.chooseFileLink}
          onClick={open}
        >
          choose file
        </Link>
      </Text>
    </div>
  );

  if (isDragActive) {
    dropState = 'active';
    message = 'Release to drop the files here';
  }

  if (isDragReject || uploadErrorMessage) {
    dropState = 'reject';
  }

  if (isDragReject) {
    message = 'File type not accepted, try again!';
  }

  if (isDragAccept) {
    dropState = 'accept';
    message = 'File accepted, release to drop!';
  }
  return (
    <div
      className={layoutStyles.uploadStack}
      style={
        {
          '--file-upload-height': fileUploadHeight ?? '100%',
        } as CSSProperties
      }
    >
      <Callout variant="info">
        <span className={styles.calloutRow}>
          <RevelioTooltip
            label={requiredColumnsTooltip}
            placement="top-start"
            openDelay={375}
          >
            <span>
              Make sure your file includes the following required columns
            </span>
          </RevelioTooltip>
          <span>
            Download{' '}
            <Link
              color="normal"
              underline="rest"
              className={styles.sampleLink}
              onClick={() => window.open(sampleFileUrl)}
            >
              sample CSV
            </Link>{' '}
            file
          </span>
        </span>
      </Callout>
      <div
        {...getRootProps()}
        className={styles.dropzone}
        data-drop-state={dropState}
      >
        {loading ? (
          <div className={layoutStyles.processingState}>
            <Loading size="sm" />
            <Text fontSize="sm" className={layoutStyles.processingText}>
              Processing file, please wait...
            </Text>
          </div>
        ) : (
          <>
            <input {...getInputProps()} />
            <div>{message}</div>
          </>
        )}
      </div>

      {uploadErrorMessage && (
        <Callout variant="warning">
          <strong className={styles.calloutTitle}>Upload Error</strong>{' '}
          {uploadErrorMessage}
        </Callout>
      )}
    </div>
  );
};

export default UploadFileStep;
